/**
 * React Query hooks for Job Description operations.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import jobDescriptionService from '../services/job-description.service';
import type { JobDescription, JobDescriptionUpdateInput } from '../types';

interface UseJDListQueryParams {
  workspaceId: string | undefined;
  idToken: string | undefined;
  jobId: string | undefined;
  enabled?: boolean;
  refetchInterval?: number | false;
}

interface UseJDDetailQueryParams {
  workspaceId: string | undefined;
  idToken: string | undefined;
  jobId: string | undefined;
  jdId: string | undefined;
  enabled?: boolean;
  /** When true, pauses background polling (use while an edit form is open). */
  pausePolling?: boolean;
}

export const useAllJDListQuery = ({
  workspaceId,
  idToken,
  enabled = true,
  q,
  statuses,
  jobIds,
  refetchInterval: refetchIntervalOverride,
}: {
  workspaceId: string | undefined;
  idToken: string | undefined;
  enabled?: boolean;
  q?: string;
  statuses?: string[];
  jobIds?: string[];
  /** Fixed poll interval in ms. If omitted, only polls while JDs are processing. */
  refetchInterval?: number | false;
}) => {
  return useQuery({
    queryKey: ['job-descriptions-all', workspaceId, q, statuses, jobIds],
    queryFn: async () => {
      if (!workspaceId || !idToken) {
        throw new Error('Workspace ID and token are required');
      }
      return jobDescriptionService.listAllJobDescriptions(workspaceId, idToken, {
        q: q || undefined,
        statuses: statuses?.length ? statuses : undefined,
        jobIds: jobIds?.length ? jobIds : undefined,
      });
    },
    enabled: enabled && !!workspaceId && !!idToken,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchInterval: refetchIntervalOverride !== undefined
      ? refetchIntervalOverride
      : (query) => {
          const items = (query.state.data as Awaited<ReturnType<typeof jobDescriptionService.listAllJobDescriptions>> | undefined)?.data?.items;
          return items?.some(jd => jd.processing_status === 'processing' || jd.processing_status === 'pending')
            ? 5000
            : false;
        },
    placeholderData: keepPreviousData,
  });
};

export const useJDListQuery = ({
  workspaceId,
  idToken,
  jobId,
  enabled = true,
  refetchInterval = 5000,
}: UseJDListQueryParams) => {
  return useQuery({
    queryKey: ['job-descriptions', workspaceId, jobId],
    queryFn: async () => {
      if (!workspaceId || !idToken || !jobId) {
        throw new Error('Workspace ID, token, and job ID are required');
      }
      return jobDescriptionService.listJobDescriptions(workspaceId, idToken, jobId);
    },
    enabled: enabled && !!workspaceId && !!idToken && !!jobId,
    staleTime: 2000,
    refetchInterval,
  });
};

export const useJDDetailByIdQuery = ({
  workspaceId,
  idToken,
  jdId,
  enabled = true,
  pausePolling = false,
}: {
  workspaceId: string | undefined;
  idToken: string | undefined;
  jdId: string | undefined;
  enabled?: boolean;
  pausePolling?: boolean;
}) => {
  return useQuery({
    queryKey: ['job-description', workspaceId, jdId],
    queryFn: async () => {
      if (!workspaceId || !idToken || !jdId) {
        throw new Error('Workspace ID, token, and JD ID are required');
      }
      return jobDescriptionService.getJobDescriptionById(workspaceId, idToken, jdId);
    },
    enabled: enabled && !!workspaceId && !!idToken && !!jdId,
    staleTime: 2000,
    refetchInterval: pausePolling
      ? false
      : (query) => {
          const status = (query.state.data as JobDescription | undefined)?.processing_status;
          return status === 'processing' || status === 'pending' ? 5000 : false;
        },
  });
};

export const useJDDetailQuery = ({
  workspaceId,
  idToken,
  jobId,
  jdId,
  enabled = true,
  pausePolling = false,
}: UseJDDetailQueryParams) => {
  return useQuery({
    queryKey: ['job-description', workspaceId, jobId, jdId],
    queryFn: async () => {
      if (!workspaceId || !idToken || !jobId || !jdId) {
        throw new Error('Workspace ID, token, job ID, and JD ID are required');
      }
      return jobDescriptionService.getJobDescription(workspaceId, idToken, jobId, jdId);
    },
    enabled: enabled && !!workspaceId && !!idToken && !!jobId && !!jdId,
    staleTime: 2000,
    // Poll only while AI is actively processing; stop once done or when an edit form is open.
    refetchInterval: pausePolling
      ? false
      : (query) => {
          const status = (query.state.data as JobDescription | undefined)?.processing_status;
          return status === 'processing' || status === 'pending' ? 5000 : false;
        },
  });
};

interface UseUploadJDMutationParams {
  workspaceId: string;
  idToken: string;
  jobId: string;
}

export const useUploadJDMutation = ({
  workspaceId,
  idToken,
  jobId,
}: UseUploadJDMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { name?: string; file?: File; raw_content?: string }) =>
      jobDescriptionService.uploadJobDescription(workspaceId, idToken, jobId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-descriptions', workspaceId, jobId] });
      queryClient.invalidateQueries({ queryKey: ['job-descriptions-all', workspaceId] });
    },
  });
};

interface UseDeleteJDMutationParams {
  workspaceId: string;
  idToken: string;
  jobId: string;
}

export const useDeleteJDMutation = ({
  workspaceId,
  idToken,
  jobId,
}: UseDeleteJDMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jdId: string) =>
      jobDescriptionService.deleteJobDescription(workspaceId, idToken, jobId, jdId),
    onSuccess: (_, jdId) => {
      queryClient.invalidateQueries({ queryKey: ['job-descriptions', workspaceId, jobId] });
      queryClient.invalidateQueries({ queryKey: ['job-descriptions-all', workspaceId] });
      queryClient.removeQueries({ queryKey: ['job-description', workspaceId, jdId] });
    },
  });
};

interface UseJDFileUrlQueryParams {
  workspaceId: string | undefined;
  idToken: string | undefined;
  jobId: string | undefined;
  jdId: string | undefined;
  enabled?: boolean;
}

export const useJDFileUrlQuery = ({
  workspaceId,
  idToken,
  jobId,
  jdId,
  enabled = false,
}: UseJDFileUrlQueryParams) => {
  return useQuery({
    queryKey: ['jd-file-url', workspaceId, jobId, jdId],
    queryFn: async () => {
      if (!workspaceId || !idToken || !jobId || !jdId) {
        throw new Error('Missing required parameters');
      }
      return jobDescriptionService.getJDFileUrl(workspaceId, idToken, jobId, jdId);
    },
    enabled: enabled && !!workspaceId && !!idToken && !!jobId && !!jdId,
    staleTime: 50 * 60 * 1000, // 50 min — slightly under the 60 min signed URL expiry
    gcTime: 55 * 60 * 1000,
  });
};

interface UseUploadJDFileMutationParams {
  workspaceId: string;
  idToken: string;
  jobId: string;
}

export const useUploadJDFileMutation = ({
  workspaceId,
  idToken,
  jobId,
}: UseUploadJDFileMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jdId, file }: { jdId: string; file: File }) =>
      jobDescriptionService.uploadJDFile(workspaceId, idToken, jobId, jdId, file),
    onSuccess: (_, { jdId }) => {
      queryClient.invalidateQueries({ queryKey: ['job-descriptions', workspaceId, jobId] });
      queryClient.invalidateQueries({ queryKey: ['job-descriptions-all', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['job-description', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['jd-file-url', workspaceId, jobId, jdId] });
    },
  });
};

interface UseReparseMutationParams {
  workspaceId: string;
  idToken: string;
  jobId: string;
}

export const useReparseMutation = ({
  workspaceId,
  idToken,
  jobId,
}: UseReparseMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jdId: string) =>
      jobDescriptionService.reparseJobDescription(workspaceId, idToken, jobId, jdId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-description', workspaceId] });
    },
  });
};

interface UseUpdateJDMutationParams {
  workspaceId: string;
  idToken: string;
  jobId: string;
}

export const useUpdateJDMutation = ({
  workspaceId,
  idToken,
  jobId,
}: UseUpdateJDMutationParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ jdId, data }: { jdId: string; data: JobDescriptionUpdateInput }) =>
      jobDescriptionService.updateJobDescription(workspaceId, idToken, jobId, jdId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-descriptions', workspaceId, jobId] });
      queryClient.invalidateQueries({ queryKey: ['job-descriptions-all', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['job-description', workspaceId] });
    },
  });
};
